import type { ComponentProps } from 'react';
import { cn } from '../lib/cn.ts';
import type { Space } from '../lib/scale.ts';

export type StackAlign = 'start' | 'center' | 'end' | 'stretch' | 'baseline';
export type StackJustify = 'start' | 'center' | 'end' | 'between' | 'around';

/**
 * Class maps are written out in full on purpose — Tailwind only emits a utility
 * it has literally seen in a source file, so `gap-${gap}` would compile to
 * nothing and leave the layout silently flat. Every map in this package follows
 * that rule.
 */
const gaps: Record<Space, string> = {
  none: 'gap-0',
  xs: 'gap-1',
  sm: 'gap-2',
  md: 'gap-4',
  lg: 'gap-6',
  xl: 'gap-10',
};

const aligns: Record<StackAlign, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
  baseline: 'items-baseline',
};

const justifies: Record<StackJustify, string> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
  around: 'justify-around',
};

export interface StackProps extends ComponentProps<'div'> {
  direction?: 'column' | 'row';
  gap?: Space;
  align?: StackAlign;
  justify?: StackJustify;
  wrap?: boolean;
}

/**
 * One-dimensional layout: a column by default, a row when asked.
 *
 * Deliberately the *only* flex primitive. A separate `Row` and `Column` reads
 * better in isolation but doubles the surface for responsive variants later,
 * and `direction` is the axis people actually change while designing.
 */
export function Stack({
  direction = 'column',
  gap = 'md',
  align,
  justify,
  wrap = false,
  className,
  ...props
}: StackProps) {
  return (
    <div
      className={cn(
        'flex',
        direction === 'row' ? 'flex-row' : 'flex-col',
        gaps[gap],
        align && aligns[align],
        justify && justifies[justify],
        wrap && 'flex-wrap',
        className,
      )}
      {...props}
    />
  );
}
